import { createSelector } from 'reselect';

const cruiseListSelector = state => state.cruises.cruises;
const filterGroupsSelector = state => state.filter.filterGroups;

const filterByDepartureMonth = (departureMonths, cruise) => {
  if (
    !departureMonths
    || departureMonths.length < 1
    || !cruise.cruiseSortAndFilterValues.departureMonths
    || departureMonths.some(id => cruise.cruiseSortAndFilterValues.departureMonths.some(month => month.id === id))
  ) {
    return true;
  }
  return false;
};

const filterByShipRating = (shipRatings, cruise) => {
  if (!shipRatings || shipRatings.length <= 0 || shipRatings.includes(cruise.cruiseSortAndFilterValues.shipRating)) {
    return true;
  }
  return false;
};

const filterByDuration = (lowValue, highValue, cruise) => {
  const { duration } = cruise.cruiseSortAndFilterValues;
  if (lowValue == null || highValue == null || (duration >= lowValue && duration <= highValue)) {
    return true;
  }
  return false;
};

const filterByCruiseCategory = (cruiseCategories, cruise) => {
  if (!cruiseCategories || cruiseCategories.length < 1 || cruiseCategories.includes(cruise.cruiseSortAndFilterValues.cruiseCategory.id)) {
    return true;
  }
  return false;
};

const filterByDestination = (destinations, cruise) => {
  if (!destinations || destinations.length < 1 || destinations.some(id => cruise.cruiseSortAndFilterValues.destinations.some(dest => dest.id === id))) {
    return true;
  }
  return false;
};

const filterByNumberOfDestinations = (lowValue, highValue, cruise) => {
  const { numberOfDestinations } = cruise.cruiseSortAndFilterValues;
  if (lowValue == null || highValue == null || (numberOfDestinations >= lowValue && numberOfDestinations <= highValue)) {
    return true;
  }
  return false;
};

const filterByShippingCompany = (shippingCompanies, cruise) => {
  if (!shippingCompanies || shippingCompanies.length < 1 || shippingCompanies.includes(cruise.cruiseSortAndFilterValues.shippingCompany.id)) {
    return true;
  }
  return false;
};


const getFilterActive = (filterGroup) => {
  const destinationIsActive = !!filterGroup.destination.selected?.length;
  const numberOfDestinationsIsActive = filterGroup.destination.selectedMinNumberOfDestinations != null
  && filterGroup.destination.selectedMaxNumberOfDestinations != null
  && (filterGroup.destination.selectedMinNumberOfDestinations > filterGroup.destination.minNumberOfDestinations
  || filterGroup.destination.selectedMaxNumberOfDestinations < filterGroup.destination.maxNumberOfDestinations)

  const filterActive = {
    departureMonth: !!filterGroup.departureMonth.selected?.length,
    shipRating: !!filterGroup.shipRating.selected?.length,
    duration: filterGroup.duration.selectedMinValue != null 
      && filterGroup.duration.selectedMaxValue != null
      && (filterGroup.duration.selectedMinValue > filterGroup.duration.minValue || filterGroup.duration.selectedMaxValue < filterGroup.duration.maxValue),
    cruiseCategory: !!filterGroup.cruiseCategory.selected?.length,
    destination: destinationIsActive || numberOfDestinationsIsActive,
    shippingCompany: !!filterGroup.shippingCompany.selected?.length,
  };
  return filterActive;
};

const filterByAllFilterGroups = (filterGroups, cruisePackages, filterForMatches = false) => {
  const filterActive = getFilterActive(filterGroups);
  const filterGroupValues = Object.values(filterGroups);
  let filteredCruisePackages = cruisePackages;
  for (let i = 0; i < filterGroupValues.length; i += 1) {
    const filterGroupValue = filterGroupValues[i];
    if (filterActive[filterGroupValue.id] || filterForMatches) {
      switch (filterGroupValue.id) {
        case 'departureMonth': {
          filteredCruisePackages = filteredCruisePackages.filter(filterByDepartureMonth.bind(null, filterGroupValue.selected));
          break;
        }
        case 'shipRating': {
          filteredCruisePackages = filteredCruisePackages.filter(filterByShipRating.bind(null, filterGroupValue.selected));
          break;
        }
        case 'duration': {
          filteredCruisePackages = filteredCruisePackages
          .filter(filterByDuration.bind(null, filterGroupValue.selectedMinValue, filterGroupValue.selectedMaxValue));
          break;
        }
        case 'cruiseCategory': {
          filteredCruisePackages = filteredCruisePackages.filter(filterByCruiseCategory.bind(null, filterGroupValue.selected));
          break;
        }
        case 'destination': {
          filteredCruisePackages = filteredCruisePackages
          .filter(filterByNumberOfDestinations.bind(null, filterGroupValue.selectedMinNumberOfDestinations, filterGroupValue.selectedMaxNumberOfDestinations));
  
          filteredCruisePackages = filteredCruisePackages.filter(filterByDestination.bind(null, filterGroupValue.selected));
          break;
        }
        case 'shippingCompany': {
          filteredCruisePackages = filteredCruisePackages.filter(filterByShippingCompany.bind(null, filterGroupValue.selected));
          break;
        }
        default:
          break;
      }
    }
  }
  return filteredCruisePackages;
};

export const filterActiveSelector = createSelector(
  [filterGroupsSelector],
  (filterGroup) => {
    const filterActive = getFilterActive(filterGroup);
    return filterActive;
  },
);

export const filteredCruisePackagesSelector = createSelector(
  [cruiseListSelector, filterGroupsSelector],
  (cruisePackages, filterGroups) => {
    if (!cruisePackages || cruisePackages.length < 1) {
      return cruisePackages;
    }
    return filterByAllFilterGroups(filterGroups, cruisePackages);
  },
);

export const filteredCruisePackagesCountSelector = createSelector(
  [filteredCruisePackagesSelector],
  (cruisePackages) => {
    if (!cruisePackages) {
      return 0;
    }
    return cruisePackages.length;
  },
);

export const filterMatchesSelector = createSelector(
  [cruiseListSelector, filterGroupsSelector],
  (cruisePackages, filterGroups) => {
    const matches = {
      departureMonth: {},
      shipRating: {},
      cruiseCategory: {},
      destination: {},
      shippingCompany: {},
    };

    // DepartureMonth
    for (let i = 0; i < filterGroups.departureMonth.options?.length; i += 1) {
      const newFilterGroups = {
        ...filterGroups,
        departureMonth: {
          ...filterGroups.departureMonth,
          selected: [filterGroups.departureMonth.options[i].id],
        },
      };

      matches.departureMonth[newFilterGroups.departureMonth.options[i].id] = filterByAllFilterGroups(newFilterGroups, cruisePackages, true).length;
    }

    // ShipRating
    for (let i = 0; i < filterGroups.shipRating.options.length; i += 1) {
      const newFilterGroups = {
        ...filterGroups,
        shipRating: {
          ...filterGroups.shipRating,
          selected: [filterGroups.shipRating.options[i]],
        },
      };

      matches.shipRating[newFilterGroups.shipRating.options[i]] = filterByAllFilterGroups(newFilterGroups, cruisePackages, true)?.length;
    }

    // CruiseCategory
    for (let i = 0; i < filterGroups.cruiseCategory.options.length; i += 1) {
      const newFilterGroups = {
        ...filterGroups,
        cruiseCategory: {
          ...filterGroups.cruiseCategory,
          selected: [filterGroups.cruiseCategory.options[i].id],
        },
      };

      matches.cruiseCategory[newFilterGroups.cruiseCategory.options[i].id] = filterByAllFilterGroups(newFilterGroups, cruisePackages, true).length;
    }

    // Destination
    for (let i = 0; i < filterGroups.destination.options.length; i += 1) {
      const newFilterGroups = {
        ...filterGroups,
        destination: {
          ...filterGroups.destination,
          selected: [filterGroups.destination.options[i].id],
        },
      };

      matches.destination[newFilterGroups.destination.options[i].id] = filterByAllFilterGroups(newFilterGroups, cruisePackages, true).length;
    }

    // ShippingCompany
    for (let i = 0; i < filterGroups.shippingCompany.options.length; i += 1) {
      const newFilterGroups = {
        ...filterGroups,
        shippingCompany: {
          ...filterGroups.shippingCompany,
          selected: [filterGroups.shippingCompany.options[i].id],
        },
      };

      matches.shippingCompany[newFilterGroups.shippingCompany.options[i].id] = filterByAllFilterGroups(newFilterGroups, cruisePackages, true).length;
    }

    return matches;
  },
);
